import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FLAGS } from '../lib/flags';
import {
  User,
  Link2,
  Target,
  Brain,
  Bell,
  Sparkles,
  Shield,
  CreditCard,
  HelpCircle,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';

// Import section components
import AccountSection from '../components/settings/AccountSection';
import ConnectedAccountsSection from '../components/settings/ConnectedAccountsSection';
import FocusControlsSection from '../components/settings/FocusControlsSection';
import MemorySection from '../components/settings/MemorySection';
import NotificationsSection from '../components/settings/NotificationsSection';
import AIBehaviorSection from '../components/settings/AIBehaviorSection';
import DataPrivacySection from '../components/settings/DataPrivacySection';
import BillingSection from '../components/settings/BillingSection';
import HelpSection from '../components/settings/HelpSection';

const SettingsPageNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract section from URL (e.g., /settings/account)
  const pathSegments = location.pathname.split('/');
  const urlSection = pathSegments[2] || 'account';
  
  const [activeSection, setActiveSection] = useState(urlSection);

  const sections = [
    { id: 'account', label: 'Account', icon: User, component: AccountSection },
    { id: 'connected-accounts', label: 'Connected Accounts', icon: Link2, component: ConnectedAccountsSection },
    { id: 'focus', label: 'Focus Controls', icon: Target, component: FocusControlsSection },
    { id: 'memory', label: 'Memory', icon: Brain, component: MemorySection },
    { id: 'notifications', label: 'Notifications', icon: Bell, component: NotificationsSection },
    { id: 'ai-behavior', label: 'AI Behavior', icon: Sparkles, component: AIBehaviorSection },
    { id: 'data-privacy', label: 'Data & Privacy', icon: Shield, component: DataPrivacySection },
    { id: 'billing', label: 'Billing', icon: CreditCard, component: BillingSection },
    { id: 'help', label: 'Help', icon: HelpCircle, component: HelpSection }
  ];

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    navigate(`/settings/${sectionId}`);
  };

  const handleBack = () => {
    // Exit settings and return to main app
    // Navigate to the main page (Swirlspace or home)
    if (FLAGS.SWIRLSPACE_ENABLED) {
      navigate('/swirlspace');
    } else {
      navigate('/');
    }
  };

  const ActiveComponent = sections.find(s => s.id === activeSection)?.component || AccountSection;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">
            Manage your account, preferences, and integrations
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="bg-white/60 backdrop-blur-lg rounded-xl border border-white/50 shadow-sm p-2">
              {sections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                
                return (
                  <button
                    key={section.id}
                    onClick={() => handleSectionChange(section.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon size={20} className="flex-shrink-0" />
                      <span className="font-medium whitespace-nowrap">{section.label}</span>
                    </div>
                    {isActive && <ChevronRight size={18} className="flex-shrink-0" />}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <div className="bg-white/60 backdrop-blur-lg rounded-xl border border-white/50 shadow-sm p-6 lg:p-8">
              <ActiveComponent />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default SettingsPageNew;

